import { useState, useEffect } from 'react';
import ActivityLog from './ActivityLog';

const EXTERNAL_RESOURCES = [
  { name: 'Azure Portal', url: 'https://portal.azure.com', description: 'Cloud hosting, storage, and deployment' },
  { name: 'Google Cloud Console', url: 'https://console.cloud.google.com', description: 'Gmail API, Google Maps API' },
  { name: 'Gmail', url: 'https://mail.google.com', description: 'Email import and receipt scanning' },
  { name: 'SimpleFIN', url: 'https://app.simplefin.org', description: 'Bank account and transaction sync' },
  { name: 'GitHub', url: 'https://github.com/timmhanson/meware', description: 'Source code repository' },
  { name: 'Airbnb', url: 'https://www.airbnb.com/hosting', description: 'Hosting calendar and reservations' },
  { name: 'Open-Meteo', url: 'https://open-meteo.com', description: 'Weather data API (free, no key)' },
  { name: 'Google Maps', url: 'https://console.cloud.google.com/apis/credentials', description: 'Distance Matrix API for commute' },
  { name: 'Twilio', url: 'https://console.twilio.com', description: 'SMS and WhatsApp notifications' },
  { name: 'PriceLabs', url: 'https://www.pricelabs.co', description: 'Dynamic pricing for short-term rentals' },
];

function SettingsPage({ nightModeSchedule, onSaveSchedule, onBack, buildVersion }) {
  const [schedule, setSchedule] = useState(nightModeSchedule || {
    enabled: false,
    startHour: 23,
    startMinute: 0,
    endHour: 6,
    endMinute: 30,
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (nightModeSchedule) {
      setSchedule(nightModeSchedule);
    }
  }, [nightModeSchedule]);

  async function handleSaveSchedule() {
    setSaving(true);
    try {
      await onSaveSchedule(schedule);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } finally {
      setSaving(false);
    }
  }

  function formatTime(h, m) {
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hour12 = h % 12 || 12;
    return `${hour12}:${String(m).padStart(2, '0')} ${ampm}`;
  }

  return (
    <div className="settings-page">
      <section className="card settings-section">
        <h2>Night Mode Schedule</h2>
        <p className="settings-description">
          Automatically enable night mode on the Default layout during specified hours (CST/CDT).
        </p>
        <div className="settings-row">
          <label className="settings-toggle-label">
            <input
              type="checkbox"
              checked={schedule.enabled}
              onChange={(e) => setSchedule({ ...schedule, enabled: e.target.checked })}
            />
            <span>Enable automatic night mode</span>
          </label>
        </div>
        {schedule.enabled && (
          <div className="settings-time-row">
            <div className="settings-time-group">
              <label>Start (night on)</label>
              <div className="settings-time-inputs">
                <select
                  value={schedule.startHour}
                  onChange={(e) => setSchedule({ ...schedule, startHour: parseInt(e.target.value) })}
                >
                  {Array.from({ length: 24 }, (_, i) => (
                    <option key={i} value={i}>{formatTime(i, 0).split(':')[0]} {formatTime(i, 0).split(' ')[1]}</option>
                  ))}
                </select>
                <span>:</span>
                <select
                  value={schedule.startMinute}
                  onChange={(e) => setSchedule({ ...schedule, startMinute: parseInt(e.target.value) })}
                >
                  {[0, 15, 30, 45].map((m) => (
                    <option key={m} value={m}>{String(m).padStart(2, '0')}</option>
                  ))}
                </select>
              </div>
              <span className="settings-time-preview">{formatTime(schedule.startHour, schedule.startMinute)}</span>
            </div>
            <div className="settings-time-group">
              <label>End (night off)</label>
              <div className="settings-time-inputs">
                <select
                  value={schedule.endHour}
                  onChange={(e) => setSchedule({ ...schedule, endHour: parseInt(e.target.value) })}
                >
                  {Array.from({ length: 24 }, (_, i) => (
                    <option key={i} value={i}>{formatTime(i, 0).split(':')[0]} {formatTime(i, 0).split(' ')[1]}</option>
                  ))}
                </select>
                <span>:</span>
                <select
                  value={schedule.endMinute}
                  onChange={(e) => setSchedule({ ...schedule, endMinute: parseInt(e.target.value) })}
                >
                  {[0, 15, 30, 45].map((m) => (
                    <option key={m} value={m}>{String(m).padStart(2, '0')}</option>
                  ))}
                </select>
              </div>
              <span className="settings-time-preview">{formatTime(schedule.endHour, schedule.endMinute)}</span>
            </div>
          </div>
        )}
        <div className="settings-save-row">
          <button className="manage-btn" onClick={handleSaveSchedule} disabled={saving}>
            {saving ? 'Saving...' : saved ? 'Saved!' : 'Save Schedule'}
          </button>
        </div>
      </section>

      <section className="card settings-section">
        <h2>External Resources</h2>
        <p className="settings-description">
          Services and tools connected to MeWare.
        </p>
        <div className="settings-resources-list">
          {EXTERNAL_RESOURCES.map((r) => (
            <a
              key={r.name}
              href={r.url}
              target="_blank"
              rel="noopener noreferrer"
              className="settings-resource-item"
            >
              <div className="settings-resource-name">{r.name}</div>
              <div className="settings-resource-desc">{r.description}</div>
              <span className="settings-resource-arrow">&#x2197;</span>
            </a>
          ))}
        </div>
      </section>

      <ActivityLog />

      {buildVersion && (
        <section className="card settings-section">
          <h2>Version</h2>
          <p className="settings-description">
            Build: {buildVersion}
          </p>
        </section>
      )}
    </div>
  );
}

export default SettingsPage;
